import React, { useRef } from "react";

import { mdiChevronDown } from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Dropdown from "../../dropdown/Dropdown";

import type { AppState } from "../../../reducers";
import type { DropdownRef } from "../../dropdown/Dropdown";

interface CardLanguageSelectProps {
  language: string;
  onLanguageChange: (language_uuid: string) => void;
}

export default function CardLanguageSelect({
  language,
  onLanguageChange,
}: CardLanguageSelectProps): React.JSX.Element {
  const { t } = useTranslation("languages");
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<DropdownRef>(null);

  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );

  const resolveLanguageUuid = (uuid: string): string | undefined =>
    languages.data?.find(
      (l: { language: string; uuid: string }) => l.uuid === uuid,
    )?.language;

  const streamLanguageCode = resolveLanguageUuid(language);

  const streamLanguageTranslation = t(`langs.${streamLanguageCode}`);

  const dropdownItems = languages.data.map(
    ({ language: languageCode, uuid: languageUuid }) => ({
      value: languageUuid,
      label: (
        <div className="text-muted-foreground hover:bg-muted flex w-full items-center justify-between gap-4 px-3.75 py-1.75">
          <div> {t(`langs.${languageCode}`)}</div>
          <div className="text-muted-foreground uppercase">{languageCode}</div>
        </div>
      ),
      searchValue: `${languageCode} ${t(`langs.${languageCode}`)}`,
    }),
  );

  return (
    <div className="relative">
      <div
        ref={triggerRef}
        className="bg-muted text-muted-foreground hover:bg-muted flex cursor-pointer items-center rounded px-1 py-0.5 text-[0.78125rem] font-medium"
        onClick={() => dropdownRef.current?.toggle()}
      >
        {streamLanguageTranslation} <Icon path={mdiChevronDown} size={0.625} />
      </div>
      <Dropdown
        ref={dropdownRef}
        withoutChevron
        withoutTrigger
        withSearch
        className="mt-1 [&>div>div:first-child]:text-center [&>div>div:first-child]:tracking-wider [&>div>div:first-child]:uppercase"
        externalRef={triggerRef as React.RefObject<HTMLDivElement>}
        items={dropdownItems}
        maxHeight={300}
        noResultsText={t("form:searchLanguageNoResults")}
        onClick={onLanguageChange}
        searchPlaceholder={t("form:searchLanguagePlaceholder")}
        title={t("selectLanguage")}
      />
    </div>
  );
}
